import { useFavorites } from "@Hooks/useFavorites";
import { LoginUser, useLogin } from "@Hooks/useLogin";
import { Favorite } from "@MeteoApi/index";
import { Environment } from "@Models/Environment";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

type UserContextType = {
  user?: LoginUser;
  favorites: Favorite[];
  loading: boolean;
  error: boolean;
  moveFavoriteUp: (index: number) => void;
  moveFavoriteDown: (index: number) => void;
  addFavorite: (favorite: Favorite) => void;
  deleteFavorite: (index: number) => Favorite[];
  saveFavorites: (favorites?: Favorite[]) => Promise<void>;
  setUser: (user?: LoginUser) => void;
};

const INITIAL_STATE: UserContextType = {
  user: undefined,
  favorites: [],
  loading: false,
  error: false,
  moveFavoriteUp: () => {},
  moveFavoriteDown: () => {},
  addFavorite: () => {},
  deleteFavorite: () => [],
  saveFavorites: async () => {},
  setUser: () => {},
};

const UserContext = createContext<UserContextType>(INITIAL_STATE);

type UserProviderProps = {
  children: ReactNode;
  env: Environment;
};

export const UserProvider = ({ children, env }: UserProviderProps) => {
  const [user, setUser] = useState<LoginUser | undefined>(undefined);

  const handleLogin = useCallback((user: LoginUser) => {
    setUser(user);
  }, []);

  const handleLogout = useCallback(() => {
    setUser(undefined);
  }, []);

  useLogin({
    onLogin: handleLogin,
    onLogout: handleLogout,
  });

  const {
    favorites,
    loading,
    error,
    moveFavoriteUp,
    moveFavoriteDown,
    addFavorite,
    deleteFavorite,
    saveFavorites,
  } = useFavorites(env, user);

  return (
    <UserContext.Provider
      value={{
        user,
        favorites,
        loading,
        error,
        moveFavoriteUp,
        moveFavoriteDown,
        addFavorite,
        deleteFavorite,
        saveFavorites,
        setUser,
      }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
